import { Link, makeStyles, withTheme } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@volt_developers/react-ui-components";
import {
  ChangeEvent,
  forwardRef,
  UIEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { logoutRoute, useCurrentUser } from "modules/auth";

import { useOperationState } from "../../../../hooks/operationsState";
import { enqueueSnackbarError } from "../../../../store/appSlice";
import { useTermsAndConditions } from "../../hooks/useTermsAndConditions";
import {
  acceptLicenceAgreement,
  operationPath as operationPaths,
} from "../../store";

const PRIVACY_STATEMENT_URL = "https://www.volt.io/generalprivacy";
const READ_LICENCE_THRESHOLD_IN_PX = 10;

interface AgreeAndAccessButtonProps {
  onSubmit: () => void;
  isLoading: boolean;
  disabled: boolean;
}

interface LicenceAgreementContentProps {
  onIsLicenceRead: () => void;
}

function LogoutButton() {
  const { t } = useTranslation();
  const history = useHistory();

  const onClick = () => {
    history.push(logoutRoute.path);
  };

  return (
    <Button
      variant="outlined"
      data-testid="terms-and-conditions-dialog-logout-button"
      onClick={onClick}
    >
      {t("Logout")}
    </Button>
  );
}

const AgreeAndAccessButton = forwardRef<
  HTMLSpanElement, // react-ui-component's button does not forward ref
  AgreeAndAccessButtonProps
>(function AgreeAndAccessButton({ isLoading, disabled, onSubmit }, ref) {
  const { t } = useTranslation();

  return (
    <Button
      color="primary"
      isLoading={isLoading}
      disabled={disabled}
      data-testid="terms-and-conditions-dialog-accept-button"
      onClick={onSubmit}
    >
      <span ref={ref}>{t("Agree and access")}</span>
    </Button>
  );
});

function LicenceAgreementContent({
  onIsLicenceRead: onIsRead,
}: LicenceAgreementContentProps) {
  const wrapperRef = useRef<HTMLElement>();
  const licenceRef = useRef<HTMLPreElement | null>(null);
  const {
    operationSuccess: licenceAgreementFileOperationSuccess,
  } = useOperationState(operationPaths.licenceAgreementFile);

  const classes = useStyles();
  const {
    licenceAgreement: { file },
  } = useTermsAndConditions();

  useEffect(() => {
    const wrapperHeight = wrapperRef.current?.offsetHeight ?? 0;
    const licenceHeight = licenceRef.current?.offsetHeight ?? 0;

    const licenceCantBeScrolled = wrapperHeight > licenceHeight;

    if (licenceAgreementFileOperationSuccess && licenceCantBeScrolled) {
      onIsRead();
    }
  }, [wrapperRef, licenceRef, onIsRead, licenceAgreementFileOperationSuccess]);

  if (!file) {
    return (
      <DialogContent
        classes={{ root: classes.dialogContent }}
        data-testid="terms-and-conditions-dialog-content"
      >
        <Skeleton width="100%" />
      </DialogContent>
    );
  }

  const handleScroll = ({
    currentTarget: { scrollHeight, scrollTop, clientHeight },
  }: UIEvent<HTMLDivElement>) => {
    const bottom =
      scrollHeight - scrollTop < clientHeight + READ_LICENCE_THRESHOLD_IN_PX;

    if (bottom) {
      onIsRead();
    }
  };

  const parsedContent = file.content
    .trim()
    .split("\n")
    .filter(Boolean)
    .join("\n\n");

  return (
    <DialogContent
      classes={{ root: classes.dialogContent }}
      innerRef={wrapperRef}
      onScroll={handleScroll}
    >
      <LicenceAgreementContentTitle>{file.title}</LicenceAgreementContentTitle>
      <pre className={classes.pre} ref={licenceRef}>
        {parsedContent}
      </pre>
    </DialogContent>
  );
}

export function TermsAndConditionsDialog() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { currentUser } = useCurrentUser();

  const [isLicenceRead, setIsLicenceRead] = useState(false);
  const [isLicenceAccepted, setIsLicenceAccepted] = useState(false);

  const buttonRef = useRef<HTMLSpanElement>(null);

  const operationPath = operationPaths.acceptLicenceAgreement;
  const { operationLoading: acceptLicenceAgreementLoading } = useOperationState(
    operationPath
  );

  const { t } = useTranslation();

  const doesUserNeedToUpdateAcceptance = Boolean(
    currentUser?.metadata?.termsAcceptanceDate
  );

  const dialogTitle = doesUserNeedToUpdateAcceptance
    ? t(
        "Action required: we\u2019ve updated our evaluation licence agreement",
        { nsSeparator: "|" }
      )
    : t("Evaluation licence agreement for Volt Sandbox environment");

  const onSubmit = () => {
    dispatch(acceptLicenceAgreement());
  };

  const onCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIsLicenceAccepted(event.target.checked);

    if (event.target.checked && buttonRef.current) {
      buttonRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const onCheckboxClick = (event: MouseEvent) => {
    const isLinkClicker = event.target instanceof HTMLAnchorElement;

    if (!isLicenceRead && !isLinkClicker) {
      dispatch(
        enqueueSnackbarError({ code: t("Read licence agreement first") })
      );
    }
  };

  return (
    <Dialog
      classes={{ paper: classes.paper }}
      data-testid="terms-and-conditions-dialog"
      open
    >
      <StyledDialogTitle data-testid="terms-and-conditions-dialog-title">
        {dialogTitle}
      </StyledDialogTitle>
      <StyledDialogSubtitle data-testid="terms-and-conditions-dialog-subtitle">
        {t(
          "Please read carefully before accessing the Volt Sandbox Environment"
        )}
      </StyledDialogSubtitle>
      <StyledDialogDescription data-testid="terms-and-conditions-dialog-description">
        {t(
          `To access the Volt Sandbox Environment, you must agree to the Volt Evaluation Licence which will bind you and the company which you are accessing the Volt Sandbox Environment on behalf of (the “Company”). Please ensure you are authorised to enter into the agreement on behalf of the Company. If you do not agree to the terms of the agreement, or are not authorised to do so, you should not click the accept button below and you may not use the Volt Sandbox Environment. The Volt Sandbox Environment is not for personal use and you should not register to use it if you are a consumer.`
        )}
      </StyledDialogDescription>

      <LicenceAgreementContent onIsLicenceRead={() => setIsLicenceRead(true)} />
      <StyledCheckbox
        data-testid="terms-and-conditions-dialog-checkbox"
        variant="outlined"
        label={
          <Trans i18nKey="licenceAgreementDialogCheckboxLabel" t={t}>
            I have read and agree to the Volt Evaluation Licence and{" "}
            <Link component="a" href={PRIVACY_STATEMENT_URL} target="_blank">
              privacy statement
            </Link>
            , and I am of legal age and confirm that I am authorised to enter
            into this agreement on the Company’s behalf, and bind the Company to
            its terms and conditions.
          </Trans>
        }
        disabled={!isLicenceRead}
        onChange={onCheckboxChange}
        onClick={onCheckboxClick}
      />
      <StyledButtonsWrapper>
        <LogoutButton />
        <AgreeAndAccessButton
          ref={buttonRef}
          isLoading={acceptLicenceAgreementLoading}
          disabled={!isLicenceAccepted}
          onSubmit={onSubmit}
        />
      </StyledButtonsWrapper>
    </Dialog>
  );
}

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(6),
    maxWidth: 640,
    color: "#56606B",
  },
  pre: {
    whiteSpace: "pre-line",
    fontSize: 12,
    fontWeight: "normal",
    margin: 0,
  },
  dialogContent: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    minHeight: "30vh",
    maxHeight: 288,
  },
}));

const StyledDialogTitle = withTheme(styled(DialogTitle)`
  padding: 0;

  h3 {
    color: #363a40;
    text-align: left;
    margin: ${({ theme }) => theme.spacing(0, 0, 3, 0)};
  }
`);

const StyledButtonsWrapper = withTheme(styled.div`
  margin: ${({ theme }) => theme.spacing(3, 0, 0, 0)};

  button {
    margin: ${({ theme }) => theme.spacing(0, 3, 0, 0)};
  }
`);

const StyledCheckbox = withTheme(styled(Checkbox)`
  margin: ${({ theme }) => theme.spacing(0, 0, 3, 0)};

  .MuiIconButton-root {
    align-self: flex-start;
  }

  .MuiFormControlLabel-label {
    line-height: 16px;
    font-size: 12px;
  }
`);

const StyledDialogSubtitle = withTheme(styled.div`
  margin: ${({ theme }) => theme.spacing(0, 0, 1.5, 0)};
  font-size: 16px;
  line-height: 32px;
  font-weight: 600;
  color: #363a40;
  font-family: ${(props) => props.theme.typography.fontFamily};
`);

const StyledDialogDescription = withTheme(styled.div`
  margin: ${({ theme }) => theme.spacing(0, 0, 3, 0)};
  font-family: ${(props) => props.theme.typography.fontFamily};
  font-size: 12px;
  line-height: 16px;
  font-weight: normal;
`);
const LicenceAgreementContentTitle = withTheme(styled.div`
  margin: ${({ theme }) => theme.spacing(0, 0, 3, 0)};
  font-family: ${(props) => props.theme.typography.fontFamily};
`);
